import type {
  FileInfo,
  SparxListResponse,
} from "@exagens/behavioral-insight-console-client";
import { z } from "zod";

/**
 * Response shape from GET /api/sparxseditor when the server returns both lists.
 * `excludedFiles`: sparx the server does not include in the editable list because the backend
 * excludes those whose metadata.traits include a category trait with name "Month Overview";
 * the frontend uses this list for the "Sparx that cannot be edited" modal.
 *
 * Matches `@exagens/behavioral-insight-console-client` `SparxListResponse`.
 */

/**
 * Parses raw API response from GET /api/sparxseditor (list of editor items).
 * Invalid shapes throw — failures propagate on the catalog subscription observable.
 */

const fileInfoSchema = z.object({
  fileName: z.string().min(1),
  title: z.string(),
  key: z.string().min(1),
  lastModified: z.number().finite().optional(),
});

const fileInfoListSchema = z.array(fileInfoSchema);

const parseFileInfoArray = (arr: unknown[]): FileInfo[] =>
  fileInfoListSchema.parse(arr);

export const parseSparxsEditorListResponse = (
  raw: unknown,
): SparxListResponse => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error(
      "GET /api/sparxseditor list response must be a JSON object with a files array",
    );
  }
  const obj = raw as Record<string, unknown>;
  if (!Array.isArray(obj.files)) {
    throw new Error(
      "GET /api/sparxseditor list response must include a files array",
    );
  }
  const excludedRaw = obj.excludedFiles;
  const excludedFiles = Array.isArray(excludedRaw)
    ? parseFileInfoArray(excludedRaw)
    : [];
  return {
    files: parseFileInfoArray(obj.files),
    excludedFiles,
  };
};
